import type { PhoneAccount } from 'services/auth'
import { trueErrorMessage } from 'utils/error'

export const NO_PHONE_ACCOUNT_MESSAGE =
	'No account signs in with this phone number. Sign in with your email or username instead, or reset your password.'

/** Used when a 429 has no readable Retry-After (missing, or not exposed to the browser by CORS). */
const DEFAULT_RETRY_AFTER_SECONDS = 60

export type PhoneAccountLookupResult =
	| { kind: 'found'; accounts: PhoneAccount[] }
	| { kind: 'none' }
	| { kind: 'rateLimited'; retryAt: Date }
	| { kind: 'error'; message: string }

/** `Retry-After` is either delay-seconds or an HTTP date. */
export function retryAfterToDate(header: unknown, now: number = Date.now()): Date {
	const value = typeof header === 'string' ? header.trim() : ''
	if (/^\d+$/.test(value)) return new Date(now + Number(value) * 1000)
	const date = value ? Date.parse(value) : Number.NaN
	if (Number.isFinite(date) && date > now) return new Date(date)
	return new Date(now + DEFAULT_RETRY_AFTER_SECONDS * 1000)
}

export function interpretPhoneAccounts(accounts: unknown): PhoneAccountLookupResult {
	// Anything but a list (e.g. an HTML page from a misrouted proxy) must not read as "no account"
	if (!Array.isArray(accounts)) return { kind: 'error', message: trueErrorMessage(undefined) }
	return accounts.length ? { kind: 'found', accounts: accounts as PhoneAccount[] } : { kind: 'none' }
}

/** Turnstile refusals from phone/accounts: token missing, or verification failed. */
export const TURNSTILE_ERROR_CODES = new Set(['400_User_043', '403_User_001'])

export const TURNSTILE_FAILED_MESSAGE = "We couldn't confirm you're not a robot. Please try again."
export const INVALID_PHONE_MESSAGE = 'Enter a valid phone number, including the country code.'

/** `error` is the axios response the `api` instance rejects with. */
export function interpretPhoneAccountsError(error: any, now: number = Date.now()): PhoneAccountLookupResult {
	if (TURNSTILE_ERROR_CODES.has(error?.data?.code)) return { kind: 'error', message: TURNSTILE_FAILED_MESSAGE }
	// Plain-text 400 for a number the backend can't parse
	if (error?.status === 400 && typeof error?.data === 'string' && /invalid phone number/i.test(error.data)) {
		return { kind: 'error', message: INVALID_PHONE_MESSAGE }
	}
	if (error?.status === 429) {
		const headers = error.headers ?? {}
		return { kind: 'rateLimited', retryAt: retryAfterToDate(headers['retry-after'] ?? headers['Retry-After'], now) }
	}
	return { kind: 'error', message: trueErrorMessage(error?.data) }
}

/**
 * A 400 from phone/start or phone/verify is account resolution refusing the handle: it expired, or the account was
 * removed or changed number. A controller refusal comes back as a plain-text body, so the status is the signal; a
 * coded body carries the code too. A 429, a 5xx or a dropped connection is transient, and retrying this screen still
 * works.
 */
export const isAccountRefusal = (error: any): boolean =>
	error?.status === 400 && (typeof error?.data === 'string' || typeof error?.data?.code === 'string')

/** Hint shown when the user switches from the phone code to email + password. */
export function emailSignInHint(accounts: PhoneAccount[]): string {
	const emails = accounts.map(account => account.maskedEmail).filter(Boolean)
	return emails.length === 1
		? `Enter the email for ${emails[0]}, then your password.`
		: 'Enter your account email, then your password.'
}
